import { faCopy } from "@fortawesome/free-regular-svg-icons";
import {
  faCheck,
  faChevronDown,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import { faEnvelope } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mui/material";
import { SCRIBENOTE_WEB } from "@src/constants";
import {
  buildReferralEmailUrl,
  EMAIL_PROVIDERS,
  getReferralEmailContent,
} from "@src/utils/referral";
import mixpanel from "mixpanel-browser";
import React, { useEffect, useId, useRef, useState } from "react";

import { alert } from "./Alert";

export default function InviteLink({ referralCode, userName = "" }) {
  const [isCopied, setIsCopied] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const menuId = useId();
  const dropdownId = `invite-link-menu-${menuId}`;
  const signupUrl = `${SCRIBENOTE_WEB}/auth/register?referralCode=${referralCode}`;

  const emailOptions = Object.entries(EMAIL_PROVIDERS).map(
    ([key, { name }]) => ({
      id: `email-${key}`,
      label: name,
      description:
        key === "default"
          ? "Opens your default mail app"
          : `Compose in ${name}`,
      onClick: () => handleEmailProviderSelect(key),
    }),
  );

  const actionSections = [
    {
      id: "clipboard",
      title: "Copy to clipboard",
      icon: faCopy,
      items: [
        {
          id: "copy-link",
          label: "Copy Referral Link",
          description: "Just the signup link — paste anywhere",
          onClick: handleCopyLink,
        },
        {
          id: "copy-message",
          label: "Copy Full Message",
          description: "Pre-written intro plus your link",
          onClick: handleCopyShareMessage,
        },
      ],
    },
    {
      id: "email",
      title: "Send via email",
      icon: faEnvelope,
      items: emailOptions,
    },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsCopied(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [isCopied]);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleCopyLink() {
    navigator.clipboard.writeText(signupUrl).then(() => {
      setIsCopied(true);
      alert(
        "info",
        "Copied Pay It Furward link. Now share it with the world to start earning credits to help pets in need! 🌎",
      );
      mixpanel.track("Referral Link Clicked/Copied", {
        referralCode,
      });
    });
    setIsMenuOpen(false);
  }

  function handleCopyShareMessage() {
    const { body } = getReferralEmailContent(signupUrl, userName);
    navigator.clipboard.writeText(body).then(() => {
      alert(
        "info",
        "Copied your Pay It Furward message. Paste it anywhere to invite a colleague!",
      );
      mixpanel.track("Referral Message Copied", {
        referralCode,
      });
    });
    setIsMenuOpen(false);
  }

  function handleEmailProviderSelect(provider) {
    mixpanel.track("Referral Link Emailed", {
      referralCode,
      provider,
    });
    const url = buildReferralEmailUrl(provider, signupUrl, userName);
    const popup = window.open(url, "_blank", "noopener,noreferrer");
    popup?.focus();
    setIsMenuOpen(false);
  }

  return (
    <div className="flex flex-col gap-md p-md xl:flex-row xl:items-center items-end">
      {/* Original link display */}
      <Tooltip
        arrow
        placement="bottom"
        title={isCopied ? "Copied!" : "Click to copy link"}
      >
        <button
          type="button"
          className="text-brand text-sm font-medium"
          onClick={handleCopyLink}
        >
          <div className="bg-secondary border border-secondary rounded-standard px-md py-sm text-xs md:text-sm text-primary transition-all hover:border-brand focus-visible:ring-2 focus-visible:ring-indigo-300">
            <span aria-live="polite" data-testid="referral-code">
              {referralCode}
            </span>
            <FontAwesomeIcon
              className="text-brand ml-sm"
              icon={isCopied ? faCheck : faCopy}
              aria-hidden="true"
            />
          </div>
        </button>
      </Tooltip>

      {/* Share with a colleague dropdown */}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          aria-controls={dropdownId}
          className="flex items-center gap-sm px-md py-sm bg-brand text-white rounded-full font-semibold text-sm shadow-xs transition-all hover:opacity-90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-300"
        >
          <FontAwesomeIcon icon={faLink} aria-hidden="true" />
          <span className="whitespace-nowrap">Send it!</span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`text-xs transition-transform ${
              isMenuOpen ? "rotate-180" : ""
            }`}
            aria-hidden="true"
          />
        </button>

        {/* Dropdown menu */}
        {isMenuOpen && (
          <div
            ref={menuRef}
            id={dropdownId}
            role="menu"
            className="absolute -left-32 mt-2 w-64 bg-primary border border-secondary rounded-large shadow-xl overflow-hidden z-dropdown"
          >
            <div className="p-sm space-y-sm">
              {actionSections.map((section, sectionIdx) => (
                <div key={section.id}>
                  {sectionIdx > 0 && (
                    <div className="my-xs">
                      <div className="h-px bg-tertiary" />
                    </div>
                  )}

                  {section.title && (
                    <p className="px-md py-1 text-base text-secondary font-semibold">
                      <FontAwesomeIcon icon={section.icon} />{" "}
                      {section.title}
                    </p>
                  )}

                  <div className="flex flex-col gap-xs">
                    {section.items.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        role="menuitem"
                        onClick={item.onClick}
                        className="w-full text-left px-lg py-sm rounded-standard hover:bg-tertiary transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand"
                      >
                        <div className="flex flex-col gap-xxs">
                          <span className="font-semibold text-primary">
                            {item.label}
                          </span>
                          {item.description && (
                            <span className="text-xs text-secondary leading-snug">
                              {item.description}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
